import React, {
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import DataTable, {
  ColumnDef,
  DataTableProps,
  sorting,
} from "veneer/DataTable";
import Stack from "veneer/Stack";

import { formatDateTimeLocal } from "cortex/ui/src/ui/table/formats";

import GroupByMenu, { GroupByMenuTrigger } from "./GroupByMenu";

export type GroupByOption = {
  dateFormat?: string;
  id: string;
  label: string;
  sourceColumnId?: string;
};

export type GroupedTableRow<T> = {
  count: number;
  groupId: string;
  groupBy: string[];
  items: ReadonlyArray<T>;
  representative: T;
  [colId: string]: unknown;
};

type GroupByTableProps<T> = DataTableProps<T> & {
  enableChipsDrag?: boolean;
  groupBy?: string[];
  groupByOptions: ReadonlyArray<GroupByOption>;
  groupedColumns?: ReadonlyArray<ColumnDef<GroupedTableRow<T>, any>>;
  hideGroupByControls?: boolean;
  initialGroupBy?: string[];
  onGroupByChange?: (value: string[]) => void;
  renderGroupedExpandedRow?: (row: GroupedTableRow<T>) => ReactNode;
  setGroupedRows?: (rows: ReadonlyArray<GroupedTableRow<T>>) => void;
};

const GROUP_KEY_SEP = "\u001F";

function getColumnValue<T>(colDef: ColumnDef<T, any>, row: T): unknown {
  const ak = (colDef as any).accessorKey as string | undefined;
  if (ak) return (row as any)?.[ak];

  const af = (colDef as any).accessorFn as ((row: T) => unknown) | undefined;
  if (af) return af(row);

  const id = (colDef as any).id as string | undefined;
  if (id) return (row as any)?.[id];

  return undefined;
}

export default function GroupByTable<T>({
  columns,
  enableChipsDrag,
  getRowProps,
  groupBy,
  groupByOptions,
  groupedColumns: groupedColumnsOverride,
  hideColumns,
  hideGroupByControls = false,
  initialGroupBy,
  primaryActions,
  renderGroupedExpandedRow,
  rows,
  setGroupedRows,
  ...rest
}: GroupByTableProps<T>) {
  const [internalGrouping, setInternalGrouping] = useState<string[]>(
    initialGroupBy ?? [],
  );
  const sortedGroupOptions = useMemo(
    () => [...groupByOptions].sort((a, b) => a.label.localeCompare(b.label)),
    [groupByOptions],
  );
  const grouping = groupBy ?? internalGrouping;
  const isGrouped = grouping.length > 0;
  const [groupByMenuAnchorPosition, setGroupByMenuAnchorPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);

  const setGrouping = useCallback(
    (next: string[]) => {
      if (groupBy === undefined) {
        setInternalGrouping(next);
      }
    },
    [groupBy],
  );

  const colDefById = useMemo(() => {
    const map = new Map<string, ColumnDef<T, any>>();
    for (const c of columns) {
      const id = String((c as any).id ?? (c as any).accessorKey ?? "");
      if (id) map.set(id, c);
    }
    return map;
  }, [columns]);

  const optionById = useMemo<Map<string, GroupByOption>>(
    () => new Map(groupByOptions.map((opt: GroupByOption) => [opt.id, opt])),
    [groupByOptions],
  );

  const groupedTableRows: ReadonlyArray<GroupedTableRow<T>> = useMemo(() => {
    if (grouping.length === 0) return [];

    const map = new Map<string, GroupedTableRow<T>>();

    for (const item of rows) {
      const keyParts: string[] = [];
      const rowValues: Record<string, unknown> = {};

      for (const optionId of grouping) {
        const option = optionById.get(optionId);
        const sourceColumnId = option?.sourceColumnId ?? optionId;
        const colDef = colDefById.get(sourceColumnId);

        const raw = colDef
          ? getColumnValue(colDef, item)
          : (item as any)?.[sourceColumnId];
        rowValues[optionId] = raw;
        rowValues[sourceColumnId] = raw;

        const dateFormat = option?.dateFormat;

        // If dateFormat is provided, group by the formatted bucket (e.g. "Feb 16th, 2026")
        if (dateFormat) {
          keyParts.push(String(formatDateTimeLocal(raw, dateFormat)));
        } else if (raw instanceof Date) {
          keyParts.push(raw.toISOString());
        } else if (raw == null) {
          keyParts.push("");
        } else {
          keyParts.push(String(raw));
        }
      }

      const groupId = keyParts.join(GROUP_KEY_SEP);
      const existing = map.get(groupId);

      if (existing) {
        (existing.items as T[]).push(item);
        existing.count += 1;
      } else {
        map.set(groupId, {
          count: 1,
          groupId,
          groupBy: grouping,
          items: [item],
          representative: item,
          ...rowValues,
        });
      }
    }

    return Array.from(map.values());
  }, [colDefById, grouping, rows, optionById]);

  useEffect(() => {
    if (!setGroupedRows) {
      return;
    }
    setGroupedRows(groupedTableRows);
  }, [groupedTableRows, setGroupedRows]);

  const groupedColumns: ReadonlyArray<ColumnDef<GroupedTableRow<T>, any>> =
    useMemo(() => {
      const cols: Array<ColumnDef<GroupedTableRow<T>, any>> = [];
      const colCount = grouping.length + 1;
      const equalWidth = `${100 / colCount}%`;

      for (const optionId of grouping) {
        const option = optionById.get(optionId);
        const sourceColumnId = option?.sourceColumnId ?? optionId;
        const original = colDefById.get(sourceColumnId);
        if (!original) {
          continue;
        }

        const dateFormat = option?.dateFormat;

        cols.push({
          ...(original as any),
          accessorKey: optionId,
          header: option?.label ?? (original as any).header,
          id: optionId,

          cell: (prop: any) => {
            const v = prop.getValue();

            if (dateFormat) {
              return formatDateTimeLocal(v, dateFormat);
            }

            const originalCell = (original as any).cell;
            if (typeof originalCell === "function") {
              return originalCell({
                ...prop,
                row: {
                  ...prop.row,
                  original: prop.row.original.representative,
                },
              });
            }

            return v;
          },
          meta: {
            ...((original as any).meta ?? {}),
            width: equalWidth,
          },
        } as ColumnDef<GroupedTableRow<T>, any>);
      }

      cols.push({
        id: "count",
        header: "Total",
        accessorKey: "count",
        meta: { align: "center", width: equalWidth },
        sortingFn: sorting.number((row: GroupedTableRow<T>) => row.count),
      });

      return cols;
    }, [colDefById, grouping, optionById]);

  const handleSetGrouping = useCallback(
    (colId: string) => {
      if (grouping.includes(colId)) {
        setGrouping(grouping.filter((group: string) => group !== colId));
      } else {
        setGrouping([...grouping, colId]);
      }
    },
    [grouping, setGrouping],
  );

  const handleClearGrouping = useCallback(() => {
    setGrouping([]);
  }, [setGrouping]);

  const handleGroupByMenuOpen = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const rect = event.currentTarget.getBoundingClientRect();
      setGroupByMenuAnchorPosition({
        left: rect.left,
        top: rect.top,
      });
    },
    [],
  );

  const handleGroupByMenuClose = useCallback(() => {
    setGroupByMenuAnchorPosition(null);
  }, []);

  const groupByMenu = hideGroupByControls ? undefined : (
    <GroupByMenu
      anchorPosition={groupByMenuAnchorPosition}
      grouping={grouping}
      onClear={handleClearGrouping}
      onClose={handleGroupByMenuClose}
      onToggle={handleSetGrouping}
      open={groupByMenuAnchorPosition != null}
      options={sortedGroupOptions}
    />
  );

  const tableInitialSort = isGrouped
    ? (rest.initialSort ?? { id: "count", desc: true })
    : rest.initialSort;
  const resolvedGroupedColumns = groupedColumnsOverride ?? groupedColumns;
  const groupedTableKey = `grouped:${grouping.join("|")}:${resolvedGroupedColumns
    .map((column) => {
      const id = (column as any).id ?? (column as any).accessorKey ?? "";
      return String(id);
    })
    .join("|")}`;
  const tablePrimaryActions = (
    <Stack alignItems="center" direction="row" gap={2}>
      {!hideGroupByControls && (
        <GroupByMenuTrigger
          grouping={grouping}
          onOpen={handleGroupByMenuOpen}
        />
      )}
      {primaryActions}
    </Stack>
  );

  if (isGrouped) {
    const groupedProps = {
      ...(rest as unknown as Omit<
        DataTableProps<GroupedTableRow<T>>,
        "columns" | "getIdentifier" | "renderExpandedRow" | "rows"
      >),
      columns: resolvedGroupedColumns,
      getIdentifier: (row: GroupedTableRow<T>) => row.groupId,
      hideColumns,
      initialSort: tableInitialSort,
      primaryActions: tablePrimaryActions,
      renderExpandedRow:
        renderGroupedExpandedRow ??
        ((row: GroupedTableRow<T>) => (
          <DataTable
            columns={columns}
            hideColumns={hideColumns}
            initialPageSize={10}
            rows={row.items}
          />
        )),
      rows: groupedTableRows,
    } as DataTableProps<GroupedTableRow<T>>;

    return (
      <>
        <DataTable<GroupedTableRow<T>>
          key={groupedTableKey}
          {...groupedProps}
        />
        {groupByMenu}
      </>
    );
  }

  return (
    <>
      <DataTable<T>
        {...rest}
        columns={columns}
        getRowProps={getRowProps}
        hideColumns={hideColumns}
        initialSort={tableInitialSort}
        primaryActions={tablePrimaryActions}
        rows={rows}
      />
      {groupByMenu}
    </>
  );
}
